import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, ArrowUpRight, CircleCheck, ShieldCheck, Heart, Sun } from "lucide-react";
import { toast } from "sonner";
import { FadeIn } from "@/components/motion";
import { API, GIVING_URL, DONATE_AMOUNTS, AMOUNT_NOTES, DESIGNATIONS, DREAM_TIERS, usePageMeta } from "@/lib/site";

const EASE = [0.22, 1, 0.36, 1];

const Donate = () => {
  usePageMeta(
    "Donate — Hope Centers International",
    "Give once or monthly to feed and educate children through Hope Centers feeding programs and Dream Release."
  );

  const [params] = useSearchParams();
  const initialDesignation = useMemo(() => {
    const q = params.get("program");
    const match = DESIGNATIONS.find((d) => d.slug === q);
    return match ? match.label : DESIGNATIONS[0].label;
  }, [params]);

  const initialIsDream = initialDesignation === "Dream Release";
  const [step, setStep] = useState(1);
  const [frequency, setFrequency] = useState(initialIsDream ? "once" : "monthly");
  const [amount, setAmount] = useState(initialIsDream ? 150 : 50);
  const [custom, setCustom] = useState("");
  const [designation, setDesignation] = useState(initialDesignation);
  const [tier, setTier] = useState(initialIsDream ? "Elementary" : "");
  const [form, setForm] = useState({ name: "", email: "", message: "", anonymous: false });
  const [submitting, setSubmitting] = useState(false);
  const [receipt, setReceipt] = useState(null);

  const isDream = designation === "Dream Release";
  const effectiveAmount = custom ? Number(custom) : amount;
  const validAmount = Number.isFinite(effectiveAmount) && effectiveAmount > 0;
  const dreamNote = DREAM_TIERS.find((t) => t.label === tier)?.note;
  const impactNote = isDream
    ? dreamNote || "Choose a sponsorship level below."
    : !custom && AMOUNT_NOTES[amount]
      ? AMOUNT_NOTES[amount]
      : "Every dollar goes straight to the field.";

  const pickDesignation = (label) => {
    setDesignation(label);
    setCustom("");
    if (label === "Dream Release") {
      setTier("Elementary");
      setAmount(150);
      setFrequency("once");
    } else {
      setTier("");
      setAmount(50);
    }
  };
  const validDetails = form.name.trim().length >= 2 && /^\S+@\S+\.\S+$/.test(form.email);

  const submit = async () => {
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/donations`, {
        amount: effectiveAmount,
        frequency,
        designation,
        sponsorship_tier: tier,
        donor_name: form.name.trim(),
        email: form.email.trim(),
        message: form.message.trim(),
        anonymous: form.anonymous,
      });
      setReceipt(res.data);
      setStep(3);
      toast.success("Almost done — finish your gift on our secure giving page.");
    } catch (e) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const stepLabel = ["Choose your gift", "Your details", "Done"];

  return (
    <div data-testid="donate-page" className="pt-32 md:pt-44 pb-24 md:pb-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <FadeIn y={16}>
          <p className="label">Donate</p>
          <h1
            data-testid="donate-heading"
            className="mt-6 font-serif font-medium text-5xl sm:text-6xl md:text-7xl tracking-tighter leading-[0.98] max-w-3xl"
          >
            Every gift becomes a <em className="text-terra">meal, a book, a future.</em>
          </h1>
        </FadeIn>

        {/* Stepper */}
        <FadeIn delay={0.2}>
          <ol className="mt-12 flex items-center gap-4 sm:gap-8" data-testid="donate-stepper">
            {stepLabel.map((label, i) => {
              const n = i + 1;
              const active = step === n;
              const done = step > n;
              return (
                <li key={label} className="flex items-center gap-3">
                  <span
                    className={`grid place-items-center w-9 h-9 rounded-full text-sm font-bold transition-colors duration-300 ${
                      done ? "bg-terra text-paper" : active ? "bg-ink text-paper" : "bg-paper-deep text-ink-soft"
                    }`}
                  >
                    {done ? <CircleCheck size={16} /> : n}
                  </span>
                  <span className={`hidden sm:block text-sm font-semibold ${active ? "text-ink" : "text-ink-soft"}`}>
                    {label}
                  </span>
                </li>
              );
            })}
          </ol>
        </FadeIn>

        <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          {/* Main flow */}
          <div className="lg:col-span-7">
            <AnimatePresence mode="wait">
              {step === 1 && (
                <motion.div
                  key="step1"
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -16 }}
                  transition={{ duration: 0.45, ease: EASE }}
                  data-testid="donate-step-amount"
                >
                  {isDream ? (
                    <p
                      data-testid="donate-yearly-note"
                      className="font-mono text-xs uppercase tracking-[0.2em] text-ink-soft"
                    >
                      Sponsorships are one-time yearly gifts
                    </p>
                  ) : (
                    <div className="flex rounded-full border border-ink/20 p-1 w-fit" role="group" aria-label="Frequency">
                      {[
                        { v: "once", label: "Give once" },
                        { v: "monthly", label: "Give monthly" },
                      ].map((f) => (
                        <button
                          key={f.v}
                          type="button"
                          data-testid={`donate-frequency-${f.v}`}
                          onClick={() => setFrequency(f.v)}
                          aria-pressed={frequency === f.v}
                          className={`rounded-full px-6 py-2.5 text-sm font-bold transition-colors duration-300 ${
                            frequency === f.v ? "bg-ink text-paper" : "text-ink-soft hover:text-ink"
                          }`}
                        >
                          {f.label}
                        </button>
                      ))}
                    </div>
                  )}

                  {isDream ? (
                    <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
                      {DREAM_TIERS.map((t) => (
                        <button
                          key={t.label}
                          type="button"
                          data-testid={`donate-tier-${t.label.toLowerCase().replace(/\s/g, "-")}`}
                          onClick={() => {
                            setTier(t.label);
                            setAmount(t.amount);
                            setCustom("");
                          }}
                          aria-pressed={!custom && tier === t.label}
                          className={`rounded-2xl border p-5 text-left transition-[border-color,background-color,transform] duration-300 hover:-translate-y-0.5 ${
                            !custom && tier === t.label
                              ? "border-terra bg-terra text-paper"
                              : "border-ink/20 bg-white text-ink hover:border-terra/50"
                          }`}
                        >
                          <span className={`block font-mono text-[10px] uppercase tracking-[0.2em] ${!custom && tier === t.label ? "text-paper/70" : "text-ink-soft"}`}>
                            {t.label}
                          </span>
                          <span className="block mt-2 font-serif text-3xl font-medium tracking-tight">
                            ${t.amount}
                            <span className="text-base font-normal">/yr</span>
                          </span>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-4">
                      {DONATE_AMOUNTS.map((a) => (
                        <button
                          key={a}
                          type="button"
                          data-testid={`donate-amount-${a}`}
                          onClick={() => {
                            setAmount(a);
                            setCustom("");
                          }}
                          aria-pressed={!custom && amount === a}
                          className={`rounded-2xl border py-6 font-serif text-3xl font-medium tracking-tight transition-[border-color,background-color,transform] duration-300 hover:-translate-y-0.5 ${
                            !custom && amount === a
                              ? "border-terra bg-terra text-paper"
                              : "border-ink/20 bg-white text-ink hover:border-terra/50"
                          }`}
                        >
                          ${a}
                        </button>
                      ))}
                    </div>
                  )}

                  <div className="mt-6">
                    <label htmlFor="custom-amount" className="label block mb-2">
                      Or enter your own
                    </label>
                    <div className="flex items-center gap-2 border-b border-ink/25 focus-within:border-terra transition-colors duration-300">
                      <span className="font-serif text-3xl text-ink-soft">$</span>
                      <input
                        id="custom-amount"
                        data-testid="donate-custom-amount-input"
                        type="number"
                        min="1"
                        value={custom}
                        onChange={(e) => setCustom(e.target.value)}
                        placeholder="Custom amount"
                        className="w-full bg-transparent py-3 font-serif text-3xl outline-none placeholder:text-ink/30"
                      />
                    </div>
                  </div>

                  <p className="mt-6 font-serif italic text-xl text-terra" data-testid="donate-impact-note">
                    {impactNote}
                  </p>

                  <div className="mt-8">
                    <p className="label mb-3">Direct my gift to</p>
                    <div className="flex flex-wrap gap-3">
                      {DESIGNATIONS.map((d) => (
                        <button
                          key={d.slug}
                          type="button"
                          data-testid={`donate-designation-${d.slug}`}
                          onClick={() => pickDesignation(d.label)}
                          aria-pressed={designation === d.label}
                          className={`rounded-full border px-5 py-2.5 text-sm font-bold transition-colors duration-300 ${
                            designation === d.label
                              ? "border-ink bg-ink text-paper"
                              : "border-ink/20 text-ink hover:border-ink"
                          }`}
                        >
                          {d.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <button
                    type="button"
                    data-testid="donate-continue-button"
                    disabled={!validAmount}
                    onClick={() => setStep(2)}
                    className="btn-primary group mt-10 disabled:opacity-40 disabled:pointer-events-none"
                  >
                    Continue
                    <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </button>
                </motion.div>
              )}

              {step === 2 && (
                <motion.div
                  key="step2"
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -16 }}
                  transition={{ duration: 0.45, ease: EASE }}
                  data-testid="donate-step-details"
                >
                  <div className="space-y-7 max-w-lg">
                    <div>
                      <label htmlFor="donor-name" className="label block mb-1">
                        Full name
                      </label>
                      <input
                        id="donor-name"
                        data-testid="donate-name-input"
                        type="text"
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        placeholder="Maria Santos"
                        className="field"
                      />
                    </div>
                    <div>
                      <label htmlFor="donor-email" className="label block mb-1">
                        Email
                      </label>
                      <input
                        id="donor-email"
                        data-testid="donate-email-input"
                        type="email"
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        placeholder="you@example.org"
                        className="field"
                      />
                    </div>
                    <div>
                      <label htmlFor="donor-message" className="label block mb-1">
                        Note of encouragement <span className="normal-case tracking-normal">(optional)</span>
                      </label>
                      <textarea
                        id="donor-message"
                        data-testid="donate-message-input"
                        rows={3}
                        value={form.message}
                        onChange={(e) => setForm({ ...form, message: e.target.value })}
                        placeholder="We'll share it with the centers."
                        className="field resize-none"
                      />
                    </div>
                    <label className="flex items-center gap-3 text-sm text-ink/80 cursor-pointer">
                      <input
                        type="checkbox"
                        data-testid="donate-anonymous-checkbox"
                        checked={form.anonymous}
                        onChange={(e) => setForm({ ...form, anonymous: e.target.checked })}
                        className="w-4 h-4 accent-[#EC4849]"
                      />
                      Make my gift anonymous
                    </label>
                  </div>

                  <div className="mt-10 flex flex-wrap gap-4">
                    <button
                      type="button"
                      data-testid="donate-back-button"
                      onClick={() => setStep(1)}
                      className="btn-outline"
                    >
                      <ArrowLeft size={16} />
                      Back
                    </button>
                    <button
                      type="button"
                      data-testid="donate-complete-button"
                      disabled={!validDetails || submitting}
                      onClick={submit}
                      className="btn-primary group disabled:opacity-40 disabled:pointer-events-none"
                    >
                      {submitting ? "Saving…" : `Continue to secure giving — $${validAmount ? effectiveAmount : 0}${frequency === "monthly" ? "/mo" : ""}`}
                      <Heart size={16} fill="currentColor" />
                    </button>
                  </div>
                </motion.div>
              )}

              {step === 3 && receipt && (
                <motion.div
                  key="step3"
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, ease: EASE }}
                  data-testid="donate-success"
                  className="max-w-xl"
                >
                  <span className="grid place-items-center w-16 h-16 rounded-full bg-terra text-paper">
                    <Sun size={28} />
                  </span>
                  <h2 className="mt-8 font-serif font-medium text-5xl sm:text-6xl tracking-tighter leading-[1.0]">
                    One last step, {form.anonymous ? "friend" : form.name.split(" ")[0]}.
                  </h2>
                  <p className="mt-6 text-base sm:text-lg leading-relaxed text-ink/75">
                    Your{" "}
                    {receipt.sponsorship_tier ? "yearly sponsorship" : frequency === "monthly" ? "monthly" : "one-time"}{" "}
                    gift of{" "}
                    <strong className="text-terra">
                      ${receipt.amount}
                      {receipt.sponsorship_tier ? "/year" : frequency === "monthly" ? "/month" : ""}
                    </strong>{" "}
                    to{" "}
                    <strong>
                      {receipt.designation}
                      {receipt.sponsorship_tier ? ` — ${receipt.sponsorship_tier} student` : ""}
                    </strong>{" "}
                    is reserved under {receipt.email}. To make it real,
                    complete it on Church Center — our secure giving platform. It takes less than a minute.
                  </p>
                  <div className="mt-8">
                    <a
                      href={GIVING_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="donate-giving-button"
                      className="btn-primary group"
                    >
                      Complete your gift securely
                      <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </a>
                    <p className="mt-4 text-sm text-ink-soft">
                      Opens our official giving page in a new tab. Choose the same amount
                      {frequency === "monthly" && !receipt.sponsorship_tier ? " and select “Recurring”" : ""} to match
                      this gift.
                    </p>
                  </div>
                  <p className="mt-6 font-mono text-xs uppercase tracking-[0.25em] text-ink-soft" data-testid="donate-reference">
                    Reference · HC-{receipt.id.slice(0, 8).toUpperCase()}
                  </p>
                  <div className="mt-10 flex flex-wrap gap-4">
                    <Link to="/" data-testid="donate-success-home-button" className="btn-outline">
                      Back home
                    </Link>
                    <Link to="/programs" data-testid="donate-success-programs-button" className="btn-primary group">
                      See your gift at work
                      <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                    </Link>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Summary sidebar */}
          <FadeIn delay={0.15} className="lg:col-span-5">
            <div className="lg:sticky lg:top-28 space-y-6">
              <div className="bg-forest text-paper rounded-2xl p-8" data-testid="donate-summary-card">
                <p className="font-mono text-xs uppercase tracking-[0.25em] text-paper/50">Your gift</p>
                <p className="mt-4 font-serif text-6xl font-medium tracking-tight text-marigold">
                  ${validAmount ? effectiveAmount : 0}
                  <span className="text-2xl text-paper/60">
                    {tier ? " / year" : frequency === "monthly" ? " / month" : ""}
                  </span>
                </p>
                <p className="mt-3 text-sm text-paper/70">
                  {designation}
                  {tier ? ` · ${tier} sponsorship` : ""}
                </p>
                <div className="mt-6 border-t border-paper/15 pt-6">
                  <p className="font-serif italic text-xl leading-snug text-paper/85">{impactNote}</p>
                </div>
              </div>
              <div className="bg-paper-deep border border-ink/10 rounded-2xl p-7 space-y-4">
                <p className="flex items-start gap-3 text-sm text-ink/75">
                  <ShieldCheck size={18} className="mt-0.5 shrink-0 text-terra" />
                  Secure giving via Church Center — your card details are only ever entered on our trusted giving
                  platform, never on this site.
                </p>
                <p className="flex items-start gap-3 text-sm text-ink/75">
                  <CircleCheck size={18} className="mt-0.5 shrink-0 text-terra" />
                  Hope Centers LLC is a registered 501(c)(3) — all donations are tax deductible in full or in part.
                </p>
                <p className="flex items-start gap-3 text-sm text-ink/75">
                  <Heart size={18} className="mt-0.5 shrink-0 text-terra" />
                  100% of designated gifts go directly to the program you choose.
                </p>
              </div>
            </div>
          </FadeIn>
        </div>
      </div>
    </div>
  );
};

export default Donate;
